/**
 * Material Symbols — Vuetify 3 Custom IconSet
 *
 * @version 1.0.0
 * @description Bridges Vuetify's icon system with Material Symbols via @iconify/vue.
 *
 * HOW IT WORKS:
 * 1. Vuetify receives an icon name string (e.g. "account-circle")
 * 2. Our IconSet component prepends the collection prefix: "material-symbols:account-circle"
 * 3. @iconify/vue renders the inline SVG
 *
 * ICON RESOLUTION ORDER (@iconify/vue):
 * 1. Pre-registered icons (via addIcon/addCollection in icon-bundle.ts) → instant, offline
 * 2. Iconify API fetch → requires network, great for dev
 *
 * USAGE IN TEMPLATES:
 *   <v-icon icon="home" />                          → material-symbols:home
 *   <v-icon icon="ms:home" />                       → material-symbols:home (explicit prefix)
 *   <gg-text-field prepend-inner-icon="search" />   → material-symbols:search
 *
 * VUETIFY INTERNAL ALIASES:
 *   Vuetify uses ~30 icons internally ($checkboxOn, $dropdown, etc.).
 *   These are mapped below via `msAliases` to Material Symbols equivalents.
 *
 * @see https://iconify.design/docs/icon-components/vue/
 * @see https://fonts.google.com/icons (Material Symbols browser)
 */

import { h } from 'vue'
import type { IconSet, IconAliases, IconProps } from 'vuetify'
import { Icon } from '@iconify/vue'

// =============================================================================
// Collection prefix
// =============================================================================

const COLLECTION = 'material-symbols'

// =============================================================================
// Vuetify IconSet Component
// =============================================================================

/**
 * Renders a Material Symbols icon via @iconify/vue.
 *
 * Vuetify passes `props.icon` as the raw icon name (e.g. "account-circle").
 * We prefix it with the collection name for Iconify resolution.
 *
 * If the icon string already contains a colon (e.g. "mdi:home"), it's passed
 * through as-is — this allows mixing icon sets when needed.
 */
export const ms: IconSet = {
  component: (props: IconProps) => {
    const iconName = props.icon as string

    // If already namespaced (contains ":"), pass through
    const fullIcon = iconName.includes(':')
      ? iconName
      : `${COLLECTION}:${iconName}`

    return h(props.tag, [
      h(Icon, {
        icon: fullIcon,
        disabled: props.disabled,
      }),
    ])
  },
}

// =============================================================================
// Vuetify Internal Icon Aliases → Material Symbols Mapping
// =============================================================================

/**
 * Maps Vuetify's internal icon aliases to Material Symbols icon names.
 * These are used by Vuetify components internally (checkbox, select arrow, etc.).
 *
 * Reference: https://vuetifyjs.com/en/features/icon-fonts/#creating-a-custom-icon-set
 *
 * MDI original name           → Material Symbols equivalent
 * ------------------------------------------------------------------
 */
export const msAliases: IconAliases = {
  // Navigation & Actions
  collapse: 'keyboard-arrow-up',
  complete: 'check',
  cancel: 'cancel',
  close: 'close',
  delete: 'cancel',
  clear: 'cancel',
  prev: 'chevron-left',
  next: 'chevron-right',
  expand: 'keyboard-arrow-down',
  menu: 'menu',
  dropdown: 'arrow-drop-down',
  subgroup: 'arrow-drop-down',

  // Feedback / Status
  success: 'check-circle',
  info: 'info',
  warning: 'warning',
  error: 'error',

  // Checkboxes
  checkboxOn: 'check-box',
  checkboxOff: 'check-box-outline-blank',
  checkboxIndeterminate: 'indeterminate-check-box',

  // Radio
  radioOn: 'radio-button-checked',
  radioOff: 'radio-button-unchecked',

  // Sorting
  sortAsc: 'arrow-upward',
  sortDesc: 'arrow-downward',

  // Pagination
  first: 'first-page',
  last: 'last-page',

  // Carousel
  delimiter: 'circle',

  // Ratings
  ratingEmpty: 'star-outline',
  ratingFull: 'star',
  ratingHalf: 'star-half',

  // Misc
  loading: 'progress-activity',
  unfold: 'unfold-more',
  file: 'attach-file',
  plus: 'add',
  minus: 'remove',
  edit: 'edit',
  calendar: 'calendar-today',
  eyeDropper: 'colorize',

  // Treeview
  treeviewCollapse: 'arrow-drop-down',
  treeviewExpand: 'arrow-right',

  // File & Color
  upload: 'upload',
  color: 'palette',

  // Keyboard hints (used by VKbd / shortcut display components)
  command: 'keyboard-command-key',
  ctrl: 'keyboard-control-key',
  space: 'space-bar',
  shift: 'shift',
  alt: 'keyboard-option-key',
  enter: 'keyboard-return',
  arrowup: 'arrow-upward',
  arrowdown: 'arrow-downward',
  arrowleft: 'arrow-back',
  arrowright: 'arrow-forward',
  backspace: 'backspace',
}
